import I18n from "i18next";
import { managedSurveyAssetType, ManagedSurveyAssetType } from "../constants/managedSurveyAssetTypes";
import { MeasurementComponent, RAGStatus, SubLocation } from "../enums/Result";
import { CableNaming } from "../enums/UTP2";
import { BaseMeasurement, Measurement, PanelMeasurement } from "../interfaces/Result";
import { KeysOfType } from "./KeysOfType";

interface SubLocationGridLayout {
  rows: { columns: SubLocation[] }[];
}

const cablesLayout: SubLocationGridLayout = {
  rows: [
    { columns: [SubLocation.P1_C1, SubLocation.P2_C1, SubLocation.P3_C1] },
    { columns: [SubLocation.P1_C2, SubLocation.P2_C2, SubLocation.P3_C2] },
    { columns: [SubLocation.P1_C3, SubLocation.P2_C3, SubLocation.P3_C3] },
    { columns: [SubLocation.P1_C4, SubLocation.P2_C4, SubLocation.P3_C4] },
  ],
};

const threeRowLocationLayout: SubLocationGridLayout = {
  rows: [
    {
      columns: [SubLocation.SUB_LOC_UPPER_LEFT, SubLocation.SUB_LOC_UPPER_CENTRE, SubLocation.SUB_LOC_UPPER_RIGHT],
    },
    {
      columns: [SubLocation.SUB_LOC_CENTRE_LEFT, SubLocation.SUB_LOC_CENTRE, SubLocation.SUB_LOC_CENTRE_RIGHT],
    },
    {
      columns: [SubLocation.SUB_LOC_LOWER_LEFT, SubLocation.SUB_LOC_LOWER_CENTRE, SubLocation.SUB_LOC_LOWER_RIGHT],
    },
  ],
};

const sixRowCableLayout: SubLocationGridLayout = {
  rows: [
    { columns: [SubLocation.P1_C1, SubLocation.P2_C1, SubLocation.P3_C1] },
    { columns: [SubLocation.P1_C2, SubLocation.P2_C2, SubLocation.P3_C2] },
    { columns: [SubLocation.P1_C3, SubLocation.P2_C3, SubLocation.P3_C3] },
    { columns: [SubLocation.P1_C4, SubLocation.P2_C4, SubLocation.P3_C4] },
    { columns: [SubLocation.P1_C5, SubLocation.P2_C5, SubLocation.P3_C5] },
    { columns: [SubLocation.P1_C6, SubLocation.P2_C6, SubLocation.P3_C6] },
  ],
};

const spoutsLayout: SubLocationGridLayout = {
  rows: [
    {
      columns: [SubLocation.SUB_LOC_LEFT, SubLocation.SUB_LOC_CENTRE, SubLocation.SUB_LOC_RIGHT],
    },
  ],
};

const singleRowCableLayout: SubLocationGridLayout = {
  rows: [{ columns: [SubLocation.P1_C1, SubLocation.P2_C1, SubLocation.P3_C1] }],
};

const outdoorSwitchyardLayout1: SubLocationGridLayout = {
  rows: [
    {
      columns: [
        SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L1,
        SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L2,
        SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L3,
      ],
    },
    {
      columns: [SubLocation.SUB_LOC_BUSHING_L1, SubLocation.SUB_LOC_BUSHING_L2, SubLocation.SUB_LOC_BUSHING_L3],
    },
    {
      columns: [SubLocation.SUB_LOC_STRUCTURE_L1, SubLocation.SUB_LOC_STRUCTURE_L2, SubLocation.SUB_LOC_STRUCTURE_L3],
    },
  ],
};

const outdoorSwitchyardLayout2: SubLocationGridLayout = {
  rows: [
    {
      columns: [
        SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L1,
        SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L2,
        SubLocation.SUB_LOC_CONDUCTOR_CONNECTION_L3,
      ],
    },
    {
      columns: [SubLocation.SUB_LOC_BUSHING_L1, SubLocation.SUB_LOC_BUSHING_L2, SubLocation.SUB_LOC_BUSHING_L3],
    },
    {
      columns: [SubLocation.SUB_LOC_TANK_L1, SubLocation.SUB_LOC_TANK_L2, SubLocation.SUB_LOC_TANK_L3],
    },
    {
      columns: [SubLocation.SUB_LOC_STRUCTURE_L1, SubLocation.SUB_LOC_STRUCTURE_L2, SubLocation.SUB_LOC_STRUCTURE_L3],
    },
  ],
};

const singleRowComponents = [
  MeasurementComponent.PrimaryBushing,
  MeasurementComponent.SecondaryBushing1,
  MeasurementComponent.SecondaryBushing2,
];

const sixRowComponents = [
  MeasurementComponent.PrimaryCables,
  MeasurementComponent.SecondaryCables1,
  MeasurementComponent.SecondaryCables2,
];

const outdoorSwitchyardComponents1 = [
  MeasurementComponent.IncomerCable,
  MeasurementComponent.OverheadSwitch1,
  MeasurementComponent.BusbarSection1,
  MeasurementComponent.LightningArrester,
  MeasurementComponent.OverheadSwitch2,
  MeasurementComponent.BusbarSection2,
];

const outdoorSwitchyardComponents2 = [
  MeasurementComponent.CircuitBreaker,
  MeasurementComponent.VoltageTransformer,
  MeasurementComponent.CurrentTransformer,
];

const threeRowLocationComponents = [
  MeasurementComponent.RearLower,
  MeasurementComponent.RearMiddle,
  MeasurementComponent.RearUpper,
  MeasurementComponent.FrontMiddle,
  MeasurementComponent.FrontLower,
  MeasurementComponent.CableBox,
];

const excludedFromLayoutComponents: {
  managedSurveyAssetType: ManagedSurveyAssetType;
  measurementComponent: MeasurementComponent;
}[] = [
  {
    managedSurveyAssetType: managedSurveyAssetType.Switchgear,
    measurementComponent: MeasurementComponent.CircuitBreaker,
  },
  {
    managedSurveyAssetType: managedSurveyAssetType.GisSwitchgear,
    measurementComponent: MeasurementComponent.LightningArrester,
  },
  {
    managedSurveyAssetType: managedSurveyAssetType.GisSwitchgear,
    measurementComponent: MeasurementComponent.VoltageTransformer,
  },
  {
    managedSurveyAssetType: managedSurveyAssetType.GisSwitchgear,
    measurementComponent: MeasurementComponent.CircuitBreaker,
  },
];

export function getSublocationLayoutByComponentType(
  componentType: MeasurementComponent,
  assetType?: ManagedSurveyAssetType,
) {
  if (
    excludedFromLayoutComponents.some(
      (component) => component.measurementComponent === componentType && component.managedSurveyAssetType === assetType,
    )
  )
    return undefined;
  else if (componentType === MeasurementComponent.Cables) return cablesLayout;
  else if (componentType === MeasurementComponent.Spouts) return spoutsLayout;
  else if (threeRowLocationComponents.includes(componentType)) return threeRowLocationLayout;
  else if (singleRowComponents.includes(componentType)) return singleRowCableLayout;
  else if (sixRowComponents.includes(componentType)) return sixRowCableLayout;
  else if (outdoorSwitchyardComponents1.includes(componentType)) return outdoorSwitchyardLayout1;
  else if (outdoorSwitchyardComponents2.includes(componentType)) return outdoorSwitchyardLayout2;
  else return undefined;
}

export function getTranslatedSubLocationName(
  sublocation: SubLocation,
  componentType: MeasurementComponent,
  cableNaming?: CableNaming,
): string {
  const sublocationSplit = sublocation.split("_");

  if (!sublocationSplit || sublocationSplit.length < 2) return "";

  if (
    [
      MeasurementComponent.Cables,
      MeasurementComponent.PrimaryCables,
      MeasurementComponent.PrimaryBushing,
      MeasurementComponent.SecondaryCables1,
      MeasurementComponent.SecondaryCables2,
      MeasurementComponent.SecondaryBushing1,
      MeasurementComponent.SecondaryBushing2,
    ].includes(componentType)
  )
    return I18n.t("SubLocations.cableNaming", {
      cable: sublocationSplit[1],
      namingConvention: cableNaming || CableNaming.L1L2L3,
      phase: sublocationSplit[0],
    });
  else return I18n.t(`SubLocations.${sublocation}`);
}

export function getOverallRAGStatus(panelMeasurement: PanelMeasurement) {
  let statusList: RAGStatus[] = [];

  const componentsWithSubLocations = [
    MeasurementComponent.Cables,
    MeasurementComponent.CableBox,
    MeasurementComponent.Spouts,
    MeasurementComponent.PrimaryCables,
    MeasurementComponent.SecondaryCables1,
    MeasurementComponent.SecondaryCables2,
    MeasurementComponent.IncomerCable,
    MeasurementComponent.OverheadSwitch1,
    MeasurementComponent.CircuitBreaker,
    MeasurementComponent.VoltageTransformer,
    MeasurementComponent.CurrentTransformer,
    MeasurementComponent.BusbarSection1,
    MeasurementComponent.LightningArrester,
    MeasurementComponent.OverheadSwitch2,
    MeasurementComponent.BusbarSection2,
    MeasurementComponent.RearLower,
    MeasurementComponent.RearMiddle,
    MeasurementComponent.RearUpper,
    MeasurementComponent.FrontLower,
    MeasurementComponent.FrontMiddle,
    MeasurementComponent.FrontUpper,
    MeasurementComponent.PrimaryBushing,
    MeasurementComponent.SecondaryBushing1,
    MeasurementComponent.SecondaryBushing2,
  ];

  const subLocationStatuses = componentsWithSubLocations.flatMap((component) =>
    getStatusListByComponentAndSubLocationLayout(panelMeasurement, component),
  );

  subLocationStatuses.forEach((status) => {
    status && statusList.push(status);
  });

  const componentsWithNoSublocation = [
    MeasurementComponent.CTChamber,
    MeasurementComponent.VTChamber,
    MeasurementComponent.UpperBusbars,
    MeasurementComponent.LowerBusbars,
    MeasurementComponent.CircuitBreaker,
    MeasurementComponent.PrimaryCableBox,
    MeasurementComponent.PrimaryBushing,
    MeasurementComponent.MainTank,
    MeasurementComponent.TapChanger,
    MeasurementComponent.SecondaryBushing1,
    MeasurementComponent.SecondaryCableBox1,
    MeasurementComponent.SecondaryBushing2,
    MeasurementComponent.SecondaryCableBox2,
    MeasurementComponent.Busbar1,
    MeasurementComponent.Busbar2,
    MeasurementComponent.DisconnectorSwitch1,
    MeasurementComponent.DisconnectorSwitch2,
    MeasurementComponent.CurrentTransformer1,
    MeasurementComponent.CurrentTransformer2,
    MeasurementComponent.CircuitBreaker,
    MeasurementComponent.VoltageTransformer,
    MeasurementComponent.LightningArrester,
    MeasurementComponent.GasInsulatedLine,
    MeasurementComponent.EarthingSwitch,
  ];

  const statuses = componentsWithNoSublocation.flatMap((componenent) =>
    getStatusListByComponentAndSubLocation(panelMeasurement, componenent, SubLocation.None),
  );

  statuses.forEach((status) => {
    status && statusList.push(status);
  });

  let ragStatus: RAGStatus = RAGStatus.None;

  if (statusList.some((status) => status === RAGStatus.Red)) ragStatus = RAGStatus.Red;
  else if (statusList.some((status) => status === RAGStatus.Amber)) ragStatus = RAGStatus.Amber;
  else if (statusList.some((status) => status === RAGStatus.Green)) ragStatus = RAGStatus.Green;

  return ragStatus;
}

function getStatusListByComponentAndSubLocationLayout(
  panelMeasurement: PanelMeasurement,
  measurementComponent: MeasurementComponent,
) {
  const sublocLayout = getSublocationLayoutByComponentType(measurementComponent);
  let statusList: RAGStatus[] = [];

  sublocLayout?.rows.forEach((row) => {
    row.columns.forEach((subloc) => {
      const statusListByComponentAndSubLocation = getStatusListByComponentAndSubLocation(
        panelMeasurement,
        measurementComponent,
        subloc,
      );

      statusListByComponentAndSubLocation?.forEach((statusByComponentAndLocation) => {
        statusList?.push(statusByComponentAndLocation);
      });
    });
  });

  return statusList.length > 0 ? statusList : undefined;
}

const MEASUREMENT_KEYS: KeysOfType<PanelMeasurement, BaseMeasurement[]>[] = [
  "CablePDMeasurements",
  "TEVMeasurements",
  "UHFMeasurements",
  "UltraSonicMeasurements",
  "VPISMeasurements",
];

function getStatusListByComponentAndSubLocation(
  panelMeasurement: PanelMeasurement,
  measurementComponent: MeasurementComponent,
  subloc: SubLocation,
) {
  const statusList: RAGStatus[] = MEASUREMENT_KEYS.flatMap((key) => {
    const measurementsForKey = panelMeasurement[key];
    if (!measurementsForKey) return [];

    const measurements = getSubLocationMeasurements(measurementsForKey, measurementComponent, subloc);
    return getStatusList(measurements);
  });

  return statusList.length > 0 ? statusList : undefined;
}

export function getSubLocationMeasurements(
  measurement: Measurement[],
  componentType: MeasurementComponent,
  sublocation: SubLocation,
) {
  return measurement.filter(
    (measurement) =>
      measurement.ComponentType === componentType &&
      (measurement.SubLocation === sublocation || (sublocation === SubLocation.None && !measurement.SubLocation)),
  );
}

function getStatusList(measurements: Measurement[]) {
  return measurements.map((measurement) => {
    return measurement.RAGStatus;
  });
}
